'use client'

import * as React from 'react'
import { Combobox as ComboboxPrimitive } from '@base-ui/react'
import { CheckIcon, ChevronDownIcon, LoaderCircleIcon, XIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/components/new/lib/utils'
import { Button } from '@/components/new/ui/button'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/new/ui/input-group'
import { usePopupLayerContainer } from '@/components/new/ui/popup-layer'

export type ComboboxFieldOption = {
    value: string
    label: string
    disabled?: boolean
    [key: string]: unknown
}

export type ComboboxFieldValue = string | string[] | null | undefined

export type ComboboxFieldAsyncOptions = {
    queryOptions: (options: {
        query: string
        offset: number
        limit: number
        signal: AbortSignal
    }) => Promise<{ options: ComboboxFieldOption[]; totalCount?: number; hasMore?: boolean }>
    loadOptions?: (options: { values: string[]; signal: AbortSignal }) => Promise<Array<ComboboxFieldOption | null>>
}

const Combobox = ComboboxPrimitive.Root

function ComboboxValue({ ...props }: ComboboxPrimitive.Value.Props) {
    return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />
}

function ComboboxTrigger({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) {
    return (
        <ComboboxPrimitive.Trigger
            data-slot="combobox-trigger"
            className={cn("[&_svg:not([class*='size-'])]:size-4", className)}
            {...props}
        >
            {children}
            <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4" />
        </ComboboxPrimitive.Trigger>
    )
}

function ComboboxClear({ className, ...props }: ComboboxPrimitive.Clear.Props) {
    return (
        <ComboboxPrimitive.Clear
            data-slot="combobox-clear"
            className={cn(className)}
            {...props}
            render={
                <InputGroupButton variant="ghost" size="icon-xs">
                    <XIcon className="pointer-events-none" />
                </InputGroupButton>
            }
        />
    )
}

const ComboboxInput = React.forwardRef<
    HTMLInputElement,
    Omit<ComboboxPrimitive.Input.Props, 'prefix'> & {
        showTrigger?: boolean
        showClear?: boolean
        startAddon?: React.ReactNode
        endAddon?: React.ReactNode
        prefix?: React.ReactNode
    }
>(
    (
        {
            className,
            children,
            disabled = false,
            showTrigger = true,
            showClear = false,
            startAddon,
            endAddon,
            prefix,
            ...props
        },
        ref
    ) => {
        return (
            <InputGroup className={cn('w-auto', className)}>
                {startAddon}
                {prefix && (
                    <InputGroupAddon align="inline-start" className="pl-1.5 pr-0">
                        {prefix}
                    </InputGroupAddon>
                )}
                <ComboboxPrimitive.Input ref={ref} render={<InputGroupInput disabled={disabled} />} {...props} />
                <InputGroupAddon align="inline-end">
                    {endAddon}
                    {showTrigger && (
                        <ComboboxTrigger
                            render={
                                <InputGroupButton
                                    size="icon-xs"
                                    variant="ghost"
                                    data-slot="input-group-button"
                                    className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent"
                                />
                            }
                            disabled={disabled}
                        />
                    )}
                    {showClear && <ComboboxClear disabled={disabled} />}
                </InputGroupAddon>
                {children}
            </InputGroup>
        )
    }
)

ComboboxInput.displayName = 'ComboboxInput'

function ComboboxContent({
    className,
    side = 'bottom',
    sideOffset = 6,
    align = 'start',
    alignOffset = 0,
    anchor,
    style,
    ...props
}: ComboboxPrimitive.Popup.Props &
    Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'>) {
    const popupLayerContainer = usePopupLayerContainer()

    return (
        <ComboboxPrimitive.Portal container={popupLayerContainer}>
            <ComboboxPrimitive.Positioner
                side={side}
                sideOffset={sideOffset}
                align={align}
                alignOffset={alignOffset}
                anchor={anchor}
                className="isolate z-[60]"
            >
                <ComboboxPrimitive.Popup
                    data-slot="combobox-content"
                    data-chips={!!anchor}
                    className={cn(
                        'group/combobox-content max-h-(--available-height) max-w-(--available-width) origin-(--transform-origin) bg-popover text-popover-foreground ring-foreground/10 *:data-[slot=input-group]:border-input/30 *:data-[slot=input-group]:bg-input/30 relative flex min-h-0 w-max min-w-36 flex-col overflow-hidden rounded-md shadow-md ring-1 *:data-[slot=input-group]:m-1 *:data-[slot=input-group]:mb-0 *:data-[slot=input-group]:h-8 *:data-[slot=input-group]:shadow-none',
                        className
                    )}
                    style={{ minWidth: 'var(--anchor-width)', ...style }}
                    {...props}
                />
            </ComboboxPrimitive.Positioner>
        </ComboboxPrimitive.Portal>
    )
}

function ComboboxEmpty({ className, ...props }: ComboboxPrimitive.Empty.Props) {
    return (
        <ComboboxPrimitive.Empty
            data-slot="combobox-empty"
            className={cn(
                'text-muted-foreground group-data-empty/combobox-content:flex hidden w-full justify-center py-2 text-center text-sm',
                className
            )}
            {...props}
        />
    )
}

const ComboboxList = React.forwardRef<
    React.ElementRef<typeof ComboboxPrimitive.List>,
    ComboboxPrimitive.List.Props & {
        loading?: boolean
        emptyContent?: React.ReactNode
        loadingContent?: React.ReactNode
        emptyClassName?: string
    }
>(({ className, children, loading = false, emptyContent, loadingContent, emptyClassName, ...props }, ref) => {
    const { t } = useTranslation()
    const shouldRenderEmptyState = loading || emptyContent !== undefined || loadingContent !== undefined
    const resolvedEmptyContent = emptyContent ?? t('components.control.combobox.noOptions')
    const resolvedLoadingContent = loadingContent ?? t('components.control.combobox.loading')

    return (
        <ComboboxPrimitive.List
            ref={ref}
            data-slot="combobox-list"
            className={cn(
                'no-scrollbar data-empty:p-0 max-h-[min(calc(--spacing(72)---spacing(9)),calc(var(--available-height)---spacing(9)))] scroll-py-1 overflow-y-auto overscroll-contain p-1',
                className
            )}
            {...props}
        >
            {children as unknown as React.ReactNode}
            {shouldRenderEmptyState && (
                <ComboboxEmpty className={emptyClassName}>
                    {loading ? (
                        <span className="inline-flex items-center gap-2">
                            <LoaderCircleIcon className="size-4 animate-spin" aria-hidden />
                            {resolvedLoadingContent}
                        </span>
                    ) : (
                        resolvedEmptyContent
                    )}
                </ComboboxEmpty>
            )}
        </ComboboxPrimitive.List>
    )
})
ComboboxList.displayName = 'ComboboxList'

function ComboboxItem({ className, children, ...props }: ComboboxPrimitive.Item.Props) {
    return (
        <ComboboxPrimitive.Item
            data-slot="combobox-item"
            className={cn(
                "outline-hidden data-highlighted:bg-accent data-highlighted:text-accent-foreground not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 relative flex w-full cursor-default select-none items-center gap-2 rounded-md py-1.5 pl-2 pr-8 text-sm [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
                className
            )}
            {...props}
        >
            {children}
            <ComboboxPrimitive.ItemIndicator
                render={
                    <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center">
                        <CheckIcon className="pointer-events-none" />
                    </span>
                }
            />
        </ComboboxPrimitive.Item>
    )
}

function ComboboxGroup({ className, ...props }: ComboboxPrimitive.Group.Props) {
    return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />
}

function ComboboxLabel({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) {
    return (
        <ComboboxPrimitive.GroupLabel
            data-slot="combobox-label"
            className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
            {...props}
        />
    )
}

function ComboboxCollection({ ...props }: ComboboxPrimitive.Collection.Props) {
    return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
}

function ComboboxSeparator({ className, ...props }: ComboboxPrimitive.Separator.Props) {
    return (
        <ComboboxPrimitive.Separator
            data-slot="combobox-separator"
            className={cn('bg-border -mx-1 my-1 h-px', className)}
            {...props}
        />
    )
}

const ComboboxChips = React.forwardRef<
    React.ElementRef<typeof ComboboxPrimitive.Chips>,
    React.ComponentPropsWithoutRef<typeof ComboboxPrimitive.Chips> & ComboboxPrimitive.Chips.Props
>(({ className, ...props }, ref) => {
    return (
        <ComboboxPrimitive.Chips
            ref={ref}
            data-slot="combobox-chips"
            className={cn(
                'border-input shadow-xs focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 has-aria-invalid:border-destructive has-aria-invalid:ring-3 has-aria-invalid:ring-destructive/20 dark:bg-input/30 dark:has-aria-invalid:border-destructive/50 dark:has-aria-invalid:ring-destructive/40 relative box-border flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border bg-transparent bg-clip-padding px-2.5 py-1.5 text-sm transition-[color,box-shadow] has-[>[data-slot=combobox-chip]]:px-1.5',
                className
            )}
            {...props}
        />
    )
})
ComboboxChips.displayName = 'ComboboxChips'

function ComboboxChip({
    className,
    children,
    showRemove = true,
    ...props
}: ComboboxPrimitive.Chip.Props & {
    showRemove?: boolean
}) {
    return (
        <ComboboxPrimitive.Chip
            data-slot="combobox-chip"
            className={cn(
                'bg-muted text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0 flex h-[calc(--spacing(5.5))] w-fit items-center justify-center gap-1 whitespace-nowrap rounded px-1.5 text-xs font-medium',
                className
            )}
            {...props}
        >
            {children}
            {showRemove && (
                <ComboboxPrimitive.ChipRemove
                    className="-ml-1 opacity-50 hover:opacity-100"
                    data-slot="combobox-chip-remove"
                    render={
                        <Button variant="ghost" size="icon-xs">
                            <XIcon className="pointer-events-none" />
                        </Button>
                    }
                />
            )}
        </ComboboxPrimitive.Chip>
    )
}

const ComboboxChipsInput = React.forwardRef<
    React.ElementRef<typeof ComboboxPrimitive.Input>,
    React.ComponentPropsWithoutRef<typeof ComboboxPrimitive.Input> & ComboboxPrimitive.Input.Props
>(({ className, ...props }, ref) => {
    return (
        <ComboboxPrimitive.Input
            ref={ref}
            data-slot="combobox-chip-input"
            className={cn('min-w-16 flex-1 outline-none', className)}
            {...props}
        />
    )
})
ComboboxChipsInput.displayName = 'ComboboxChipsInput'

function useComboboxAnchor() {
    return React.useRef<HTMLDivElement | null>(null)
}

export {
    Combobox,
    ComboboxInput,
    ComboboxContent,
    ComboboxList,
    ComboboxItem,
    ComboboxGroup,
    ComboboxLabel,
    ComboboxCollection,
    ComboboxEmpty,
    ComboboxSeparator,
    ComboboxChips,
    ComboboxChip,
    ComboboxChipsInput,
    ComboboxTrigger,
    ComboboxValue,
    useComboboxAnchor,
}
